/**
 * Dashboard View
 * System overview with tenant health and key metrics
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icons } from '../common/Icons';
import { systemService, tenantsService } from '../../services/api';
import { formatNumber, formatRelativeTime } from '../../utils/formatters';

function formatDue(isoDate) {
  if (!isoDate) return '';
  const [y, m, d] = String(isoDate).slice(0, 10).split('-').map(Number);
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(y, m - 1, d)));
}

const DashboardView = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [monitoring, setMonitoring] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
  try {
    setLoading(true);
    
    // Load tenants
    const tenantsData = await tenantsService.getAll({ limit: 5 }).catch(() => ({ data: [] }));
    setTenants(tenantsData.data || []);
    
    // Get dashboard stats and the five compliance-report summaries
    let statsData = null;
    try {
      const response = await systemService.getDashboardStats();
      // Handle nested data structure: response is { status, data: { totalTenants, ... } }
      statsData = response?.data || response;
    } catch (err) {
      console.warn('Dashboard stats endpoint failed:', err);
    }

    try {
      const monitoringRes = await systemService.getComplianceSummary();
      setMonitoring(monitoringRes?.data || monitoringRes);
    } catch (err) {
      console.warn('Compliance summary failed:', err);
      setMonitoring(null);
    }
    
    setStats(statsData || {
      totalTenants: 0,
      activeTenants: 0,
      totalUsers: 0,
      totalTransactions: 0,
      systemHealth: 'unknown'
    });
  } catch (err) {
    console.error('Failed to load dashboard:', err);
    setError('Failed to load dashboard data');
  } finally {
    setLoading(false);
  }
};

  if (loading) {
    return (
      <div className="empty-state">
        <Icons.Loader size={48} className="spin" />
        <p>Loading dashboard...</p>
        <style>{`.spin { animation: spin 1s linear infinite; } @keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div>
      {error && (
        <div className="alert alert-warning mb-3">
          <Icons.AlertCircle size={18} />
          <span>{error} - Showing placeholder data</span>
        </div>
      )}

      {/* Stats Grid */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon primary">
            <Icons.Building size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{formatNumber(stats?.totalTenants ?? 0)}</div>
            <div className="stat-label">Total Tenants</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon success">
            <Icons.CheckCircle size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{formatNumber(stats?.activeTenants ?? 0)}</div>
            <div className="stat-label">Active Tenants</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon warning">
            <Icons.Users size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{formatNumber(stats?.totalUsers ?? 0)}</div>
            <div className="stat-label">Total Users</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon primary">
            <Icons.Activity size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{formatNumber(stats?.totalTransactions ?? 0)}</div>
            <div className="stat-label">Transactions (30d)</div>
          </div>
        </div>

        {(stats?.pendingSignupRequests ?? 0) > 0 && (
          <div
            className="stat-card"
            style={{ cursor: 'pointer' }}
            onClick={() => navigate('/admin/signup-requests')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && navigate('/admin/signup-requests')}
          >
            <div className="stat-icon warning">
              <Icons.Mail size={24} />
            </div>
            <div className="stat-content">
              <div className="stat-value">{formatNumber(stats.pendingSignupRequests)}</div>
              <div className="stat-label">New signup requests</div>
            </div>
          </div>
        )}
      </div>

      <MonitoringStrip monitoring={monitoring} navigate={navigate} />

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '24px' }}>
        {/* Recent Tenants */}
        <div className="card">
          <div className="card-header">
            <div>
              <h3 className="card-title">Recent Tenants</h3>
              <p className="card-subtitle">Latest tenant activity</p>
            </div>
            <button 
              className="btn btn-secondary btn-sm"
              onClick={() => navigate('/admin/tenants')}
            >
              View All
              <Icons.ChevronRight size={16} />
            </button>
          </div>

          {tenants.length === 0 ? (
            <div className="empty-state" style={{ padding: '32px' }}>
              <Icons.Building size={48} />
              <h3>No tenants yet</h3>
              <p>Create your first tenant to get started</p>
              <button 
                className="btn btn-primary mt-2"
                onClick={() => navigate('/admin/onboarding')}
              >
                <Icons.Plus size={18} />
                Create Tenant
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {tenants.map(tenant => (
                <div 
                  key={tenant.id}
                  className="tenant-card"
                  onClick={() => navigate(`/admin/tenants/${tenant.id}`)}
                >
                  <div 
                    className="tenant-avatar"
                    style={{ 
                      background: tenant.is_active 
                        ? 'var(--accent-primary)' 
                        : 'var(--text-muted)' 
                    }}
                  >
                    {tenant.name?.charAt(0).toUpperCase() || 'T'}
                  </div>
                  <div className="tenant-info">
                    <div className="tenant-name">{tenant.name}</div>
                    <div className="tenant-details">
                      Created {formatRelativeTime(tenant.created_at)}
                    </div>
                  </div>
                  <span className={`badge badge-${tenant.is_active ? 'success' : 'neutral'}`}>
                    <span className={`status-dot ${tenant.is_active ? 'active' : 'inactive'}`}></span>
                    {tenant.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Quick Actions</h3>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <button 
              className="btn btn-primary"
              onClick={() => navigate('/admin/onboarding')}
              style={{ justifyContent: 'flex-start' }}
            >
              <Icons.Rocket size={18} />
              <span>Onboard New Tenant</span>
            </button>

            <button 
              className="btn btn-secondary"
              onClick={() => navigate('/admin/tenants')}
              style={{ justifyContent: 'flex-start' }}
            >
              <Icons.Building size={18} />
              <span>Manage Tenants</span>
            </button>

            <button 
              className="btn btn-secondary"
              onClick={() => navigate('/admin/signup-requests')}
              style={{ justifyContent: 'flex-start' }}
            >
              <Icons.Mail size={18} />
              <span>Review Signup Requests</span>
            </button>

            <button 
              className="btn btn-secondary"
              onClick={() => navigate('/admin/settings')}
              style={{ justifyContent: 'flex-start' }}
            >
              <Icons.Settings size={18} />
              <span>System Settings</span>
            </button>
          </div>

          <div style={{ marginTop: '24px', paddingTop: '24px', borderTop: '1px solid var(--border-color)' }}>
            <h4 style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '12px' }}>System Health</h4>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span className={`status-dot ${stats?.systemHealth === 'healthy' ? 'active' : 'pending'}`} style={{ width: '12px', height: '12px' }}></span>
              <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                {stats?.systemHealth === 'healthy' ? 'All systems operational' : 'Status unknown'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

function MonitorCard({ icon, tone, value, label, hint, onClick }) {
  return (
    <div
      className="stat-card"
      style={{ cursor: 'pointer' }}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onClick()}
    >
      <div className={`stat-icon ${tone}`}>
        {icon}
      </div>
      <div className="stat-content">
        <div className="stat-value">{value}</div>
        <div className="stat-label">{label}</div>
        {hint && (
          <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>{hint}</div>
        )}
      </div>
    </div>
  );
}

function MonitoringStrip({ monitoring, navigate }) {
  if (!monitoring) return null;
  const users = monitoring.userAccounts || {};
  const changes = monitoring.accessChanges || {};
  const acks = monitoring.acknowledgments || {};
  const score = monitoring.changeScorecard || {};
  const incidents = monitoring.incidents || {};
  const period = monitoring.period;
  const ackOutstanding = (acks.ownerMissingAck || 0) + (acks.externalOutstandingPairs || 0);
  const scoreFails = score.configured
    ? (score.issues?.fail || 0) + (score.pullRequests?.fail || 0)
    : null;
  const scoreWarns = score.configured
    ? (score.issues?.warn || 0) + (score.pullRequests?.warn || 0)
    : null;

  return (
    <div style={{ marginBottom: '24px' }}>
      <div style={{ marginBottom: '12px' }}>
        <h3 className="card-title" style={{ marginBottom: '4px' }}>Control monitoring</h3>
        <p className="card-subtitle" style={{ margin: 0 }}>
          {period
            ? `${period.label} summaries · access review due ${formatDue(period.due)}. Open a card for the full report.`
            : 'Summaries from the five compliance reports.'}
        </p>
      </div>
      <div className="stats-grid" style={{ marginBottom: 0 }}>
        <MonitorCard
          icon={<Icons.Users size={24} />}
          tone={users.error ? 'warning' : (users.privilegedMissingMfa > 0 ? 'error' : users.staleActive > 0 ? 'warning' : 'success')}
          value={users.error ? '—' : formatNumber(users.privilegedMissingMfa || 0)}
          label="Privileged without MFA"
          hint={users.error
            ? 'User accounts unavailable'
            : `${formatNumber(users.staleActive || 0)} stale · ${formatNumber((users.tenantAccounts || 0) + (users.platformUsers || 0))} accounts${(users.decryptFailed || 0) > 0 ? ` · ${formatNumber(users.decryptFailed)} names unread` : ''}`}
          onClick={() => navigate('/admin/compliance-reports#user-accounts')}
        />
        <MonitorCard
          icon={<Icons.Activity size={24} />}
          tone={changes.error ? 'warning' : 'primary'}
          value={changes.error ? '—' : formatNumber(changes.total || 0)}
          label="Access changes"
          hint={changes.error ? 'Access log unavailable' : period ? period.label : 'This quarter'}
          onClick={() => navigate('/admin/compliance-reports#access-changes')}
        />
        <MonitorCard
          icon={<Icons.FileText size={24} />}
          tone={acks.error ? 'warning' : (ackOutstanding > 0 ? 'warning' : 'success')}
          value={acks.error ? '—' : formatNumber(ackOutstanding)}
          label="Acks outstanding"
          hint={acks.error ? 'Acknowledgment rollup unavailable' : `${acks.ownerMissingAck || 0} owner · ${acks.externalOutstandingPairs || 0} accountant`}
          onClick={() => navigate('/admin/compliance-reports#acknowledgments')}
        />
        <MonitorCard
          icon={<Icons.BarChart size={24} />}
          tone={!score.configured ? 'warning' : (scoreFails > 0 ? 'error' : scoreWarns > 0 ? 'warning' : 'success')}
          value={score.configured ? formatNumber(scoreFails || 0) : '—'}
          label="Change records failing"
          hint={score.configured ? `${formatNumber(scoreWarns || 0)} warnings in the scorecard window` : 'GitHub token not set'}
          onClick={() => navigate('/admin/github-scorecard')}
        />
        <MonitorCard
          icon={<Icons.AlertCircle size={24} />}
          tone={!incidents.configured ? 'warning' : ((incidents.open || 0) > 0 ? 'error' : 'success')}
          value={incidents.configured ? formatNumber(incidents.open || 0) : '—'}
          label="Open Sev1 / Sev2"
          hint={incidents.configured
            ? `${formatNumber(incidents.openedInPeriod || 0)} opened this quarter`
            : 'GitHub token not set'}
          onClick={() => navigate('/admin/compliance-reports#incidents')}
        />
      </div>
    </div>
  );
}

export default DashboardView;
